import { defineElement, FC, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { Button } from '@atoms/button/button';
import { FriendlyCaptchaWidget, useFriendlyCaptcha } from '@features/friendly-captcha';
import { addAlert } from '@source/store/alert';
import { openModal } from '@source/store/modals';
import { updateSmartSubscriptionProject } from '@source/store/smart-subscription';
import { translateItem, useButtonLoadingState, useTranslate } from '@source/utilities/hooks';
import {
    isStoreAddressDataComplete,
    subscribeToProjectPhase,
    subscriptionStore,
} from '@store/subscription/store';

import { SUBSCRIPTION_MODAL_ID } from '../smart-subscription';

const SubscriptionBox: FC = ({ element }) => {
    const [boxTitle] = useProp<string | Text | null>('boxTitle', null);
    const [boxContent] = useProp<string | Text>('boxContent');
    const [variant] = useProp<string | null>('variant', null);
    const [buttonLabel] = useProp<string | Text>('buttonLabel');
    const [projectPhaseId] = useProp<string>('projectPhaseId');
    const [projectPhaseName] = useProp<string>('projectPhaseName');
    const [districtId] = useProp<string>('districtId');
    const [constructionNumberName] = useProp<string>('constructionNumberName', '');
    const [deleteWhenSubscribed] = useProp<boolean>('deleteWhenSubscribed', false);

    let className = 'c-subscription-box';
    className = variant ? `${className} subscription-box--${variant}` : className;

    const setSubmitButtonLoading = useButtonLoadingState('[js-hook-cta-subscription-box]');
    let isSubmitting = false;

    const { initFcWidget, getFcToken, removeFcWidget } = useFriendlyCaptcha({
        formParent: element,
        startMode: 'auto',
    });

    subscriptionStore.subscribe(
        ({ projectPhases }) => {
            if (deleteWhenSubscribed) {
                const projectPhaseIsSubscribed = !!projectPhases?.find(
                    (id) => id === projectPhaseId,
                );

                if (projectPhaseIsSubscribed) element.remove();
            }
        },
        ['projectPhases'],
    );

    async function handleSubscription() {
        if (isSubmitting) return;

        isSubmitting = true;
        setSubmitButtonLoading(true);

        try {
            const { user, address } = subscriptionStore.getState();
            const { areaProjectName, areaId } = window.__ENVIRONMENT_DETAILS__;

            if (!user || !address || !isStoreAddressDataComplete(address)) {
                throw new Error('Form.Validation.UserInformationMissing');
            }

            initFcWidget();

            updateSmartSubscriptionProject({
                districtId,
                projectPhaseId,
                title: constructionNumberName,
                area: areaProjectName,
                name: projectPhaseName,
            });

            await subscribeToProjectPhase({
                subscriptionInfo: {
                    type: 'project',
                    projectPhaseId,
                    areaId,
                    districtId,
                },
                userInfo: {
                    firstName: user.firstName || '',
                    lastName: user.lastName,
                    initials: user.initials,
                    email: user.email,
                    gender: user.gender,
                },
                fcToken: await getFcToken(),
            });

            openModal(SUBSCRIPTION_MODAL_ID);
        } catch (error) {
            let errorMsg = !!error?.message && (await translateItem(error.message));
            if (!errorMsg) errorMsg = await translateItem('Form.Submit.Failed');
            addAlert(errorMsg, 'error');
        } finally {
            removeFcWidget();
            setSubmitButtonLoading(false);
            isSubmitting = false;
        }
    }

    return (
        <div class={className}>
            <div>
                <h4 class="subscription-box__title">
                    {boxTitle
                        ? boxTitle
                        : useTranslate('SubscriptionBox.Title', {
                              replace: [{ id: 'Phase', value: projectPhaseName }],
                          })}
                </h4>

                <p class="subscription-box__description">{boxContent}</p>
            </div>

            <Button
                type="button"
                hook="cta-subscription-box"
                label={buttonLabel}
                classes="subscription-box__button"
                variant={variant === 'vertical' ? 'tertiary' : 'primary'}
                width={variant === 'vertical' ? 'full-width' : undefined}
                onClick={() => handleSubscription()}
                hasLoading
            />

            <FriendlyCaptchaWidget classes="u-hidden" />
        </div>
    );
};

SubscriptionBox.props = {
    boxTitle: {
        type: String,
    },
    boxContent: {
        type: String,
        required: true,
    },
    variant: {
        type: String,
    },
    buttonLabel: {
        type: String,
        required: true,
    },
    projectPhaseId: {
        type: String,
        required: true,
    },
    projectPhaseName: {
        type: String,
        required: true,
    },
    districtId: {
        type: String,
        required: true,
    },
    propertyTypeId: {
        type: String,
    },
    constructionNumberName: {
        type: String,
    },
    deleteWhenSubscribed: {
        type: Boolean,
    },
};

defineElement('subscription-box', SubscriptionBox);
